'use client';

import CustomAdministrativeUnit, {
    AdministrativeUnitValueOptions,
    AdministrativeUnitValues,
} from '@/components/custom-administative-unit';
import CustomButton from '@/components/custom-button';
import CustomCaptcha, { CaptChaRefs } from '@/components/custom-captcha';
import CustomDateTimePicker from '@/components/custom-datetime-picker';
import CustomDivider from '@/components/custom-divider';
import CustomInput from '@/components/custom-input';
import CustomLoading from '@/components/custom-loading';
import CustomForm, { FormItemProps } from '@/components/custom-RHF-form';
import CustomSelectGender from '@/components/custom-select-gender';
import CustomToast from '@/components/custom-toast';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import {
    regexEmail,
    regexIdentityCardNumber,
    regexPhoneNumber,
    regexSpecialCharsContains_,
} from '@/constant/regexs';
import { pageDirectories } from '@/constant/route';
import { MessageResource } from '@/i18n/messages';
import signInCredentials from '@/keys/signInCredentials';
import { cn } from '@/lib/utils';
import { Link } from '@/navigation';
import { studentRegisterAccount } from '@/server/api/v1/auth/auth.action';
import { LoginInputType } from '@/server/api/v1/auth/schema';
import {
    SignInResponse,
    StudentRegisterRequest,
} from '@/server/api/v1/auth/types';
import { Gender, LoginTarget } from '@/types/enum';
import { zodResolver } from '@hookform/resolvers/zod';
import { CircleAlert, LogIn } from 'lucide-react';
import { getSession, signIn } from 'next-auth/react';
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import {
    FieldErrors,
    useForm,
    useFormContext,
    useWatch,
} from 'react-hook-form';
import { z } from 'zod';
import { BackgroundSignIn } from './bg-sign-in';

const SignUpSchema = (
    trans: (data: keyof MessageResource['System']) => string
) => {
    return z
        .object({
            userName: z
                .string({ message: trans('System_Required') })
                .min(1, { message: trans('System_Required') })
                .regex(regexSpecialCharsContains_, {
                    message: 'Tên đăng nhập không hợp lệ',
                }),
            password: z
                .string({ message: trans('System_Required') })
                .min(1, { message: 'Mật khẩu không được để trống' }),
            confirmPassword: z
                .string({ message: trans('System_Required') })
                .min(1, { message: 'Mật khẩu không được để trống' }),
            firstName: z
                .string({ message: trans('System_Required') })
                .min(1, { message: 'Tên không được để trống' }),
            lastName: z
                .string({ message: trans('System_Required') })
                .min(1, { message: 'Họ tên không được để trống' }),
            gender: z.nativeEnum(Gender),
            dateOfBirth: z.date().nullable(),
            phoneNumber: z
                .string({ message: trans('System_Required') })
                .regex(regexPhoneNumber, {
                    message: trans('System_InvalidPhoneNumber'),
                }),
            email: z
                .string()
                .regex(regexEmail, { message: 'Email không hợp lệ' })
                .or(z.literal(''))
                .optional(),

            IDCardNumber: z
                .string({ message: trans('System_Required') })
                .regex(regexIdentityCardNumber, {
                    message: 'CMND/CCCD không hợp lệ',
                }),

            streetAddress: z
                .string({ message: trans('System_Required') })
                .min(1, { message: trans('System_Required') }),
            postalCode: z
                .string({ message: trans('System_Required') })
                .min(1, { message: trans('System_Required') }),
            commune: z
                .string({ message: trans('System_Required') })
                .min(1, { message: trans('System_Required') }),
            communeCode: z
                .string({ message: trans('System_Required') })
                .min(1, { message: trans('System_Required') }),
            province: z
                .string({ message: trans('System_Required') })
                .min(1, { message: trans('System_Required') }),
            provinceCode: z
                .string({ message: trans('System_Required') })
                .min(1, { message: trans('System_Required') }),
            country: z
                .string({ message: trans('System_Required') })
                .min(1, { message: trans('System_Required') }),
            countryCode: z
                .string({ message: trans('System_Required') })
                .min(1, { message: trans('System_Required') }),
            nationality: z
                .string({ message: trans('System_Required') })
                .min(1, { message: trans('System_Required') }),
            nationalityCode: z
                .string({ message: trans('System_Required') })
                .min(1, { message: trans('System_Required') }),
        })
        .refine((data) => data.password === data.confirmPassword, {
            message: 'Mật khẩu không khớp',
            path: ['confirmPassword'],
        });
};
interface SignIn extends z.infer<ReturnType<typeof SignUpSchema>> {}

const defaultValues: SignIn = {
    userName: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    dateOfBirth: null,
    gender: Gender.Male,
    email: '',
    phoneNumber: '',
    IDCardNumber: '',

    nationality: 'Việt Nam',
    nationalityCode: 'VN',
    country: 'Việt Nam',
    countryCode: 'VN',
    postalCode: '700000',
    commune: '',
    communeCode: '',
    province: '',
    provinceCode: '',
    streetAddress: '',
};

export const SignUpForm = () => {
    const transDef = useTranslations('System');
    const transPage = useTranslations('SignUp');
    const transAccountInfo = useTranslations('AccountInfo');
    const _capchaRef = useRef<CaptChaRefs>(null);

    const [isPassCapcha, setIsPassCapcha] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [resultResponse, setResultResponse] = useState({
        isError: false,
        message: '',
    });

    const methods = useForm<SignIn>({
        mode: 'onBlur',
        defaultValues: defaultValues,
        delayError: 200,
        resolver: zodResolver(SignUpSchema(transDef)),
    });

    const handleAfterSignin = async () => {
        const session = await getSession();

        if (session?.actionNeeded) {
            // Cần đổi mật khẩu mới trước khi đăng nhập
        } else {
            window.location.reload();
        }
    };

    const handleSignInAfterSignUp = async (res: SignInResponse) => {
        const loginBody: LoginInputType['credentials'] = {
            password: 'def',
            username: 'def',
            target: LoginTarget.With_Token,
            token: res.Token,
            refresh_token: res.RefreshToken,
            tokenType: res.TokenType,
            expiresIn: `${res.ExpiresIn}`,
        };
        const result = await signIn(signInCredentials.Credentials, {
            ...loginBody,
            redirect: false,
        });
        if (!(result?.error?.length && result.error.length > 0)) {
            await handleAfterSignin();
            return;
        } else {
            setIsSubmitting(false);
        }
    };

    const onRegister = async (data: SignIn) => {
        setIsSubmitting(true);
        const sendData: StudentRegisterRequest = {
            UserName: data.userName,
            Password: data.password,
            FirstName: data.firstName,
            LastName: data.lastName,
            FullName:
                `${data.lastName?.trim()} ${data.firstName?.trim()}`.trim(),
            Phone: data.phoneNumber,
            CitizenIdentityCardNumber: data.IDCardNumber,
            Email: data.email,
            DateOfBirth: (data.dateOfBirth ?? new Date()).toISOString(),
            Address: {
                IsDefault: true,
                Commune: data.commune,
                CommuneCode: data.communeCode,
                Country: data.country,
                CountryCode: data.countryCode,
                PostalCode: data.postalCode,
                Province: data.province,
                ProvinceCode: data.provinceCode,
                StreetAddress: data.streetAddress,
                Nationality: data.nationality,
                NationalityCode: data.nationalityCode,
            },
            IsSendEmail: false,
            ExtendedInfoJson: undefined,
        };

        const result = await studentRegisterAccount(sendData);
        if (result?.data?.isSuccessStatus && result?.data.data) {
            await handleSignInAfterSignUp(result?.data.data);
        } else {
            _capchaRef.current?.reset();
            setResultResponse({
                isError: true,
                message:
                    result?.data?.errors?.join(', ') ??
                    transDef('System_AnErrorHasOccurred'),
            });
            console.error(`Faild To signuo`, result?.data);
            setIsSubmitting(false);
        }
    };

    const onError = (e: FieldErrors<SignIn>) => {
        console.error({ e });

        CustomToast.sonnerV2({
            type: 'error',
            title: 'Vui lòng kiểm tra lại thông tin đăng ký',
        });
    };

    const rls: FormItemProps<SignIn> = [
        {
            type: 'Grid',
            wrapperClassName: 'gap-x-4 gap-y-2',
            children: [
                {
                    type: 'Item',
                    name: 'userName',
                    label: transPage('SignUp_UserName'),
                    wrapperClassName: 'col-span-full',
                    required: true,
                    children: (
                        <Input
                            placeholder={transPage('SignUp_UserName') + '. . .'}
                        />
                    ),
                },
                {
                    type: 'Item',
                    name: 'password',
                    label: transPage('SignUp_Password'),
                    wrapperClassName: 'col-span-full xl:col-span-1',
                    required: true,
                    children: (
                        <CustomInput.Password
                            placeholder={transPage('SignUp_Password') + '. . .'}
                        />
                    ),
                },
                {
                    type: 'Item',
                    name: 'confirmPassword',
                    label: transPage('SignUp_ConfirmPassword'),
                    wrapperClassName: 'col-span-full xl:col-span-1',
                    required: true,
                    children: (
                        <CustomInput.Password
                            placeholder={
                                transPage('SignUp_ConfirmPassword') + '. . .'
                            }
                        />
                    ),
                },
                {
                    type: 'Custom',
                    renderParent: () => (
                        <CustomDivider.Title
                            wrapperClassName='pb-4'
                            titleClassName='text-xl text-primary'
                            title={transAccountInfo('AccountInfo_PersonalInfo')}
                        />
                    ),
                },
                {
                    type: 'Grid',
                    wrapperClassName: 'col-span-full pl-4',
                    children: [
                        {
                            type: 'Item',
                            name: 'lastName',
                            label: transPage('SignUp_LastName'),
                            wrapperClassName: 'col-span-full xl:col-span-1',
                            required: true,
                            children: (
                                <Input
                                    placeholder={
                                        transPage('SignUp_LastName') + '. . .'
                                    }
                                />
                            ),
                        },
                        {
                            type: 'Item',
                            name: 'firstName',
                            wrapperClassName: 'col-span-full xl:col-span-1',
                            label: transPage('SignUp_FirstName'),
                            required: true,
                            children: (
                                <Input
                                    placeholder={
                                        transPage('SignUp_FirstName') + '. . .'
                                    }
                                />
                            ),
                        },
                        {
                            type: 'Item',
                            name: 'gender',
                            wrapperClassName: 'col-span-full xl:col-span-1',
                            label: transPage('SignUp_Gender'),
                            render: ({ field }) => {
                                const val =
                                    typeof field.value === 'number'
                                        ? field.value.toString()
                                        : field.value;
                                return (
                                    <CustomSelectGender.Root
                                        value={val}
                                        onChange={(val) => {
                                            field.onChange(
                                                parseInt(val) as Gender
                                            );
                                        }}
                                    />
                                );
                            },
                        },
                        {
                            type: 'Item',
                            name: 'dateOfBirth',
                            wrapperClassName: 'col-span-full xl:col-span-1',
                            label: transPage('SignUp_DateOfBirth'),
                            required: true,
                            children: (
                                <CustomDateTimePicker.Root
                                    granularity='day'
                                    placeholder='dd/mm/yyyy'
                                />
                            ),
                        },
                    ],
                },
                {
                    type: 'Custom',
                    wrapperClassName: 'col-span-full',
                    renderParent: () => (
                        <CustomDivider.Title
                            wrapperClassName='pb-4'
                            titleClassName='text-xl text-primary'
                            title={transAccountInfo(
                                'AccountInfo_ContactAddress'
                            )}
                        />
                    ),
                },
                {
                    type: 'Grid',
                    wrapperClassName: 'col-span-full pl-4',
                    children: [
                        {
                            type: 'Item',
                            name: 'phoneNumber',
                            wrapperClassName: 'col-span-full xl:col-span-1',
                            label: transPage('SignUp_Phone'),
                            required: true,
                            children: (
                                <Input
                                    placeholder={
                                        transPage('SignUp_Phone') + '. . .'
                                    }
                                />
                            ),
                        },
                        {
                            type: 'Item',
                            name: 'email',
                            wrapperClassName: 'col-span-full xl:col-span-1',
                            label: transPage('SignUp_Email'),
                            children: (
                                <Input
                                    placeholder={
                                        transPage('SignUp_Email') + '. . .'
                                    }
                                />
                            ),
                        },
                        {
                            type: 'Item',
                            name: 'IDCardNumber',
                            wrapperClassName: 'col-span-full xl:col-span-1',
                            label: transPage('SignUp_IdCardNumber'),
                            required: true,

                            children: (
                                <Input
                                    placeholder={
                                        transPage('SignUp_IdCardNumber') +
                                        '. . .'
                                    }
                                />
                            ),
                        },
                        {
                            type: 'Custom',
                            name: 'streetAddress',
                            wrapperClassName: 'pt-1',
                            renderParent: () => (
                                <div className='col-span-full pt-1'>
                                    <CustomSelectAddress />
                                </div>
                            ),
                        },
                    ],
                },
            ],
        },
    ];

    return (
        <CustomLoading.Wrapper loading={isSubmitting}>
            <div className='relative min-h-full bg-secondary/40 p-6 xl:p-24 xl:pt-8'>
                <div
                    className={cn(
                        'relative z-10 mx-auto max-w-3xl space-y-2 rounded-md bg-white p-6 shadow-md xl:p-12 xl:py-8'
                    )}
                >
                    <h1 className='mb-8 text-center text-xl font-bold uppercase'>
                        {transPage('SignUp_SignUpAsVisitor')}
                    </h1>

                    <div className='[&_input]:no-ring'>
                        <CustomForm.FormProvider {...methods}>
                            <CustomForm.Form rls={rls} />
                        </CustomForm.FormProvider>
                    </div>

                    {resultResponse.isError && (
                        <>
                            <Alert
                                className={cn('bg-red-100 py-3 text-red-600')}
                            >
                                <CircleAlert
                                    className={cn('h-4 w-4 !text-red-500')}
                                />
                                <AlertTitle className='text-sm'>
                                    {transPage(
                                        'SignUp_FailedWhenCreateAccount'
                                    )}
                                </AlertTitle>
                                <AlertDescription className='text-xs'>
                                    {resultResponse.message}
                                </AlertDescription>
                            </Alert>
                        </>
                    )}

                    <div className='items-center justify-between gap-x-5 space-y-5 md:flex'>
                        <div className='flex items-center justify-center'>
                            <CustomCaptcha.Root
                                ref={_capchaRef}
                                onChange={(isValid) => {
                                    setIsPassCapcha(isValid);
                                    if (isValid) {
                                        setResultResponse({
                                            isError: false,
                                            message: '',
                                        });
                                    }
                                }}
                            />
                        </div>
                        <div className='flex-1 space-y-2'>
                            <CustomButton.Root
                                onClick={methods.handleSubmit(
                                    onRegister,
                                    onError
                                )}
                                isLoading={isSubmitting}
                                disabled={!isPassCapcha}
                                icon={<LogIn size={20} />}
                                className='w-full gap-x-4 font-semibold'
                            >
                                {transPage('SignUp_SignUp')}
                            </CustomButton.Root>

                            <div className=''>
                                <p className='text-center text-sm text-muted-foreground'>
                                    {transPage('SignUp_HaveAnAccount')}?{' '}
                                    <Link
                                        href={pageDirectories['Signin']}
                                        className='font-medium text-primary hover:underline hover:opacity-80'
                                    >
                                        {transPage('SignUp_SignIn')}
                                    </Link>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <BackgroundSignIn />
            </div>
        </CustomLoading.Wrapper>
    );
};

const CustomSelectAddress = () => {
    const { setValue } = useFormContext<SignIn>();

    const handleChange = (
        value?: AdministrativeUnitValues,
        opt?: AdministrativeUnitValueOptions
    ) => {
        const { province, ward } = opt || {};

        setValue(`province`, province?.Name ?? '');
        setValue(`provinceCode`, province?.Origin_Def_Code ?? '');
        setValue(`commune`, ward?.Name ?? '');
        setValue(`communeCode`, ward?.Origin_Def_Code ?? '');
        setValue(`streetAddress`, value?.detailAddr ?? '');
    };

    const [provinceCode, province, communeCode, commune, streetAddress] =
        useWatch<SignIn>({
            name: [
                `provinceCode`,
                `province`,
                `communeCode`,
                `commune`,
                `streetAddress`,
            ],
        });

    return (
        <CustomAdministrativeUnit.Level2
            layout='vertical'
            showInputDetailAddress={true}
            wrapperClassName='gap-y-4 col-span-full max-xl:gap-y-2'
            itemClassName='flex-col items-start gap-y-2'
            labelClassName='[&>span]:leading-normal [&>span]:xl:leading-normal  [&>span]:required'
            onChange={handleChange}
            values={{
                provinceCode: provinceCode?.toString(),
                communeCode: communeCode?.toString(),
                commune: commune?.toString(),
                province: province?.toString(),
                detailAddr: streetAddress?.toString(),
            }}
        />
    );
};
